import React, { useState, useEffect } from 'react';
import { closeoutApi } from '../utils/closeoutApi';
import { formatCurrency } from '../utils/helpers';

function Kassenabschluss() {
  const [preview, setPreview] = useState(null);
  const [closeouts, setCloseouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [previewData, closeoutsData] = await Promise.all([
        closeoutApi.getPreviewToday(),
        closeoutApi.getAll()
      ]);
      setPreview(previewData);
      setCloseouts(closeoutsData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setMessage({ type: 'error', text: 'Fehler beim Laden: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDay = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const period_start = new Date(now.setHours(0, 0, 0, 0)).toISOString();
      const period_end = new Date(now.setHours(23, 59, 59, 999)).toISOString();

      await closeoutApi.create({
        period_start,
        period_end,
        created_by: 'admin-001'
      });

      setMessage({ type: 'success', text: '✓ Tag erfolgreich abgeschlossen!' });
      setShowConfirmModal(false);
      
      // Reload data
      setTimeout(() => {
        loadData();
        setMessage(null);
      }, 2000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Kassenabschluss
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">Tagesabschluss & Z-Berichte</p>
      </div>

      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-[var(--success-bg)] text-[var(--success-text)] border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Today's Preview */}
      {preview && (
        <div className="dark-card rounded-lg shadow border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold">Heutiger Umsatz (Vorschau)</h2>
              <p className="text-sm text-[var(--text-secondary)]">
                {new Date().toLocaleDateString('de-DE', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={loading || preview.transaction_count === 0}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="close-day-button"
            >
              📊 Tag abschließen (Z-Bericht)
            </button>
          </div>

          <div className="grid grid-cols-4 gap-4">
            {/* Total */}
            <div className="bg-[var(--accent-muted)] rounded-lg p-4 border border-blue-200">
              <div className="text-sm text-[var(--text-secondary)] uppercase tracking-wide">Gesamtumsatz</div>
              <div className="text-3xl font-bold text-[var(--accent-primary)]">{formatCurrency(preview.total_gross)}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">{preview.transaction_count} Verkäufe</div>
            </div>

            {/* Cash */}
            <div className="bg-[var(--warning-bg)] rounded-lg p-4 border border-yellow-200">
              <div className="text-sm text-[var(--text-secondary)] uppercase tracking-wide">Bar</div>
              <div className="text-3xl font-bold text-yellow-600">{formatCurrency(preview.cash_total)}</div>
            </div>

            {/* Card */}
            <div className="bg-[var(--success-bg)] rounded-lg p-4 border border-green-200">
              <div className="text-sm text-[var(--text-secondary)] uppercase tracking-wide">Karte</div>
              <div className="text-3xl font-bold text-green-600">{formatCurrency(preview.card_total)}</div>
            </div>

            {/* Refunds */}
            {preview.refund_count > 0 && (
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="text-sm text-[var(--text-secondary)] uppercase tracking-wide">Stornos</div>
                <div className="text-3xl font-bold text-red-600">{formatCurrency(preview.refund_total)}</div>
                <div className="text-xs text-[var(--text-secondary)] mt-1">{preview.refund_count} Stornos</div>
              </div>
            )}
          </div>

          {/* VAT Breakdown */}
          <div className="mt-4 p-4 bg-[var(--bg-tertiary)] rounded-lg">
            <div className="text-sm font-medium mb-2">MwSt-Übersicht:</div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-[var(--text-secondary)]">Netto</div>
                <div className="text-lg font-bold">{formatCurrency(preview.total_net)}</div>
              </div>
              <div>
                <div className="text-xs text-[var(--text-secondary)]">MwSt gesamt</div>
                <div className="text-lg font-bold">{formatCurrency(preview.total_vat)}</div>
              </div>
              <div>
                <div className="text-xs text-[var(--text-secondary)]">Nach Satz</div>
                {Object.entries(preview.vat_breakdown || {}).map(([rate, amount]) => (
                  <div key={rate} className="text-sm">
                    {rate}%: {formatCurrency(amount)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {preview.transaction_count === 0 && (
            <div className="mt-4 p-3 bg-[var(--warning-bg)] text-[var(--warning-text)] rounded-lg text-sm border border-yellow-200">
              ⚠️ Keine Transaktionen heute. Abschluss nicht möglich.
            </div>
          )}
        </div>
      )}

      {/* Past Closeouts */}
      <div className="dark-card rounded-lg shadow border p-6">
        <h2 className="text-xl font-bold mb-4">Vergangene Abschlüsse</h2>
        
        {closeouts.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-secondary)]">
            Noch keine Tagesabschlüsse vorhanden
          </div>
        ) : (
          <div className="space-y-3">
            {closeouts.map((closeout) => (
              <div
                key={closeout.id || closeout._id}
                className="border rounded-lg p-4 hover:bg-[var(--bg-tertiary)] transition-colors"
                data-testid={`closeout-${closeout.z_number}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-bold text-sm">
                      Z-{closeout.z_number.toString().padStart(4, '0')}
                    </div>
                    <div>
                      <div className="font-semibold">
                        {new Date(closeout.period_start).toLocaleDateString('de-DE')}
                      </div>
                      <div className="text-xs text-[var(--text-secondary)]">
                        {new Date(closeout.created_at).toLocaleString('de-DE')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className="text-sm text-[var(--text-secondary)]">Umsatz</div>
                      <div className="text-xl font-bold">{formatCurrency(closeout.total_gross)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-[var(--text-secondary)]">Bar</div>
                      <div className="text-lg font-semibold text-yellow-600">
                        {formatCurrency(closeout.cash_total)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-[var(--text-secondary)]">Karte</div>
                      <div className="text-lg font-semibold text-green-600">
                        {formatCurrency(closeout.card_total)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-[var(--text-secondary)]">Verkäufe</div>
                      <div className="text-lg font-semibold">{closeout.transaction_count}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="dark-card rounded-lg shadow-xl p-6 w-[500px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-4">Tag abschließen?</h2>
            
            <div className="mb-6 p-4 bg-[var(--accent-muted)] rounded-lg border border-blue-200">
              <div className="text-sm text-blue-800">
                <strong>Hinweis:</strong> Nach dem Abschluss können keine Änderungen mehr am heutigen Zeitraum vorgenommen werden.
              </div>
            </div>

            {preview && (
              <div className="mb-6 space-y-2">
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Z-Nummer:</span>
                  <span className="font-bold">Z-{preview.z_number.toString().padStart(4, '0')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Gesamtumsatz:</span>
                  <span className="font-bold text-lg">{formatCurrency(preview.total_gross)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Verkäufe:</span>
                  <span className="font-bold">{preview.transaction_count}</span>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCloseDay}
                disabled={loading}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50"
                data-testid="confirm-close-day-button"
              >
                {loading ? 'Wird abgeschlossen...' : '✓ Tag abschließen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Kassenabschluss;
